<script setup lang="ts">
	/**
	 * Pastilla de la barra de filtros (estilo Expedia): un botón compacto que abre su
	 * control en un popover, para que la barra no muestre todos los controles a la vez.
	 *
	 * - `value`: filtro de un solo valor activo («Reembolsable», «4★ o más»); la pastilla
	 *   lo muestra en lugar de la etiqueta.
	 * - `count`: filtro de opción múltiple; la pastilla muestra la etiqueta y un globo.
	 */
	import { computed } from 'vue'

	import {
		Popover,
		PopoverContent,
		PopoverTrigger,
	} from '@/components/ui/popover'

	import { RAIL_COLLISION_PADDING } from '../helpers/hotelFilters'

	const props = defineProps<{
		label: string
		icon: string
		value?: string | null
		count?: number
		/** Ancho del popover; por omisión el de una lista. */
		contentClass?: string
	}>()

	const emit = defineEmits<{ clear: []; close: [] }>()

	const isActive = computed(() => !!props.value || (props.count ?? 0) > 0)
</script>

<template>
	<Popover @update:open="(open) => !open && emit('close')">
		<PopoverTrigger as-child>
			<button
				type="button"
				class="inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-full py-1.5 pl-3 pr-2 text-[13px] font-medium ring-1 ring-inset transition-colors"
				:class="
					isActive ?
						'bg-[#021F58]/[0.06] text-[#021F58] ring-[#021F58]/40'
					:	'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50 hover:ring-gray-400'
				"
			>
				<span
					class="material-symbols-outlined shrink-0 text-[16px] leading-none"
					:class="isActive ? 'text-[#021F58]' : 'text-gray-400'"
					aria-hidden="true"
					>{{ icon }}</span
				>
				<span class="truncate">{{ value || label }}</span>
				<span
					v-if="(count ?? 0) > 0"
					class="grid h-[18px] min-w-[18px] shrink-0 place-items-center rounded-full bg-[#021F58] px-1 text-[11px] font-semibold tabular-nums text-white"
					>{{ count }}</span
				>
				<span
					class="material-symbols-outlined shrink-0 text-[18px] leading-none text-gray-400"
					aria-hidden="true"
					>expand_more</span
				>
			</button>
		</PopoverTrigger>

		<!-- collision-padding: deja libre el riel flotante del borde derecho (ver RAIL_COLLISION_PADDING). -->
		<PopoverContent
			align="start"
			:collision-padding="RAIL_COLLISION_PADDING"
			:class="[
				'flex max-w-[calc(100vw_-_6rem)] flex-col gap-3 rounded-xl p-3',
				contentClass ?? 'w-72',
			]"
		>
			<div class="flex items-center justify-between gap-2">
				<span class="text-[13px] font-semibold text-gray-900">{{ label }}</span>
				<button
					v-if="isActive"
					type="button"
					class="text-[12px] font-medium text-[#021F58]/70 hover:text-[#021F58]"
					@click="emit('clear')"
				>
					Quitar
				</button>
			</div>
			<slot />
		</PopoverContent>
	</Popover>
</template>
